import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { COUNTRY_PHONE_CODES, DEFAULT_COUNTRY } from '../../lib/countryPhoneCodes';

const SORTED_BY_DIAL_LENGTH = [...COUNTRY_PHONE_CODES].sort((a, b) => b.dial.length - a.dial.length);

function splitValue(value) {
  if (!value) return { country: DEFAULT_COUNTRY, national: '' };
  const match = SORTED_BY_DIAL_LENGTH.find((c) => value.startsWith(c.dial));
  if (!match) return { country: DEFAULT_COUNTRY, national: value.replace(/\D/g, '') };
  return { country: match, national: value.slice(match.dial.length).replace(/\D/g, '') };
}

/**
 * Standard phone-input pattern: a country dial-code picker on the left
 * (flag + code, searchable), a plain national-number field on the right.
 * Emits a single combined E.164-ish string via onChange (e.g.
 * "+2348012345678") so it drops straight into the same `phone` form field
 * a plain text input would have — this is purely a friendlier way to
 * collect the same value, not a new field.
 */
export default function PhoneInput({ label, value, onChange, error, helperText, id, name }) {
  const inputId = id || name || 'phone';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const initial = useMemo(() => splitValue(value), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [country, setCountry] = useState(initial.country);
  const [national, setNational] = useState(initial.national);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    const onKeyDown = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKeyDown);
    searchRef.current?.focus();
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const emit = (nextCountry, nextNational) => {
    onChange?.(nextNational ? `${nextCountry.dial}${nextNational}` : '');
  };

  const selectCountry = (c) => {
    setCountry(c);
    setOpen(false);
    setSearch('');
    emit(c, national);
  };

  const handleNationalChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
    setNational(digits);
    emit(country, digits);
  };

  const filtered = search.trim()
    ? COUNTRY_PHONE_CODES.filter(
        (c) => c.name.toLowerCase().includes(search.toLowerCase()) || c.dial.includes(search) || c.iso2.toLowerCase() === search.toLowerCase()
      )
    : COUNTRY_PHONE_CODES;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-foreground-muted mb-1.5">
          {label}
        </label>
      )}

      <div className="relative flex" ref={containerRef}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label="Select country code"
          aria-expanded={open}
          className={clsx(
            'flex items-center gap-1.5 rounded-l-xl border border-r-0 bg-background-elevated px-3 py-2.5 text-sm text-foreground shrink-0',
            'hover:border-border-strong transition-colors',
            error ? 'border-danger' : 'border-border'
          )}
        >
          <span className="text-base leading-none">{country.flag}</span>
          <span className="text-foreground-muted">{country.dial}</span>
          <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5 text-foreground-subtle">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <input
          id={inputId}
          name={name}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={national}
          onChange={handleNationalChange}
          placeholder="801 234 5678"
          aria-invalid={!!error}
          className={clsx(
            'w-full rounded-r-xl bg-background-elevated border px-3.5 py-2.5 text-sm text-foreground min-w-0',
            'placeholder:text-foreground-subtle transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
            error ? 'border-danger' : 'border-border focus:border-border-strong'
          )}
        />

        {open && (
          <div className="absolute left-0 top-full mt-1.5 w-72 max-h-72 overflow-hidden rounded-xl border border-border bg-background-surface shadow-card-hover z-30 flex flex-col">
            <div className="p-2 border-b border-border">
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country or code"
                className="w-full rounded-lg bg-background-elevated border border-border px-3 py-1.5 text-sm text-foreground placeholder:text-foreground-subtle focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
              />
            </div>
            <div className="overflow-y-auto py-1">
              {filtered.length === 0 && <p className="px-3 py-4 text-sm text-foreground-muted text-center">No matches.</p>}
              {filtered.map((c) => (
                <button
                  key={c.iso2}
                  type="button"
                  onClick={() => selectCountry(c)}
                  className={clsx(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-background-elevated transition-colors',
                    c.iso2 === country.iso2 ? 'text-accent' : 'text-foreground'
                  )}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-foreground-subtle">{c.dial}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
      {!error && helperText && <p className="mt-1.5 text-xs text-foreground-subtle">{helperText}</p>}
    </div>
  );
}
